import { useState } from "react";
import { toast } from "sonner";
import { Clock, ShieldCheck, ShieldQuestion, ShieldOff } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import UploadBoxV2 from "./UploadBoxV2";
import { useAutoSavePatch } from "../../lib/useAutoSavePatch";
import SaveStatus from "./SaveStatus";

/**
 * Vermittler "Identitätsnachweis" — uploads ID card front/back OR passport +
 * residence certificate, request/approve/reject cycle.
 * Dokumente werden per Auto-Save sofort gespeichert (kein „Speichern"-Button).
 */
export default function IdVerificationSection({ user, onSaved }) {
    const [uploads, setUploads] = useState({
        id_card_front_url: user.id_card_front_url || "",
        id_card_back_url: user.id_card_back_url || "",
        passport_url: user.passport_url || "",
        residence_certificate_url: user.residence_certificate_url || "",
    });
    const [requesting, setRequesting] = useState(false);
    const { save, saving, savedAt } = useAutoSavePatch("/users/me/vermittler-profile");

    const setUrl = (key, url) => { const next = { ...uploads, [key]: url }; setUploads(next); save(next); };
    const clearUrl = (key) => { const next = { ...uploads, [key]: "" }; setUploads(next); save(next); };

    const HINTS_ID_FRONT = [
        "Vorderseite mit Foto, Name und Geburtsdatum vollständig sichtbar",
        "MRZ-Zeile (unten) NICHT abdecken",
        "Tageslicht, kein Blitz — Hologramm-Spiegelung vermeiden",
        "Alle 4 Ecken im Bild · senkrecht von oben fotografieren",
    ];
    const HINTS_ID_BACK = [
        "Rückseite mit Anschrift und Behörden-Angaben vollständig zeigen",
        "MRZ-Zeile durchgehend lesbar (kein Knick, kein Daumen)",
        "Heller Hintergrund, scharfer Text",
    ];
    const HINTS_PASSPORT = [
        "Aufgeklappte Datenseite mit Foto, Name, Geburtsdatum",
        "Beide MRZ-Zeilen (unten, 2 × 44 Zeichen) lesbar",
        "Hologramm möglichst ohne Spiegelung — Pass leicht kippen wenn nötig",
        "Hintergrund neutral und ruhig (Tisch, einfarbig)",
    ];
    const HINTS_RESIDENCE = [
        "Original-Meldebescheinigung — keine Selbstauskunft, kein Foto vom Bildschirm",
        "Stempel, Unterschrift und Datum müssen gut lesbar sein",
        "Anschrift muss mit dem Profil übereinstimmen",
        "PDF bevorzugt — alternativ scharfes Foto bei Tageslicht",
    ];

    const requestVerification = async () => {
        setRequesting(true);
        try {
            await api.post("/users/me/vermittler-request-id-verification");
            await onSaved();
            toast.success("Prüfung beantragt — wir melden uns binnen 48 h.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Anfrage fehlgeschlagen");
        } finally {
            setRequesting(false);
        }
    };

    const hasIdCard = uploads.id_card_front_url && uploads.id_card_back_url;
    const hasPassport = uploads.passport_url && uploads.residence_certificate_url;
    const hasAnyVariant = hasIdCard || hasPassport;
    const status = user.id_verification_status || "none";
    const canRequest = hasAnyVariant && (status === "none" || status === "rejected");

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="vermittler-id-verification">
            <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-5 h-5 text-[#FFCC00]" />
                <h2 className="font-display font-bold text-lg">Identitätsnachweis</h2>
                <SaveStatus saving={saving} savedAt={savedAt} className="ml-auto" />
            </div>
            <p className="text-xs text-gray-600 mb-4">
                Statt einer Gewerbeanmeldung benötigen wir entweder <strong>Ausweis (Vor- &amp; Rückseite)</strong> oder <strong>Reisepass + Meldebescheinigung</strong>.
            </p>

            {status === "pending" && (
                <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-sm" data-testid="v-id-status-pending">
                    <Clock className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
                    <div className="text-xs text-amber-900">
                        <div className="font-bold">Ihre Identitätsprüfung läuft</div>
                        <div className="mt-0.5">Unser Team prüft Ihre Unterlagen. Sie erhalten in der Regel binnen 48 h eine Rückmeldung.</div>
                    </div>
                </div>
            )}
            {status === "approved" && (
                <div className="mb-4 flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-sm" data-testid="v-id-status-approved">
                    <ShieldCheck className="w-4 h-4 mt-0.5 text-green-700 shrink-0" />
                    <div className="text-xs text-green-900">
                        <div className="font-bold">Identität verifiziert ✓</div>
                        <div className="mt-0.5">Auszahlungen können jederzeit beantragt werden.</div>
                    </div>
                </div>
            )}
            {status === "rejected" && (
                <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-sm" data-testid="v-id-status-rejected">
                    <ShieldOff className="w-4 h-4 mt-0.5 text-red-700 shrink-0" />
                    <div className="text-xs text-red-900">
                        <div className="font-bold">Identitätsprüfung abgelehnt</div>
                        {user.id_verification_note && (
                            <div className="mt-0.5 italic" data-testid="v-id-rejection-note">„{user.id_verification_note}"</div>
                        )}
                        <div className="mt-1">
                            Bitte ersetzen Sie die unten markierten Dokumente und reichen Sie die Prüfung erneut ein.
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <UploadBoxV2 label="Variante A: Ausweis Vorderseite" url={uploads.id_card_front_url}
                    onUploaded={(u) => setUrl("id_card_front_url", u)}
                    onClear={() => clearUrl("id_card_front_url")}
                    hints={HINTS_ID_FRONT}
                    testid="v-upload-id-front" />
                <UploadBoxV2 label="Variante A: Ausweis Rückseite" url={uploads.id_card_back_url}
                    onUploaded={(u) => setUrl("id_card_back_url", u)}
                    onClear={() => clearUrl("id_card_back_url")}
                    hints={HINTS_ID_BACK}
                    testid="v-upload-id-back" />
                <UploadBoxV2 label="Variante B: Reisepass" url={uploads.passport_url}
                    onUploaded={(u) => setUrl("passport_url", u)}
                    onClear={() => clearUrl("passport_url")}
                    hints={HINTS_PASSPORT}
                    testid="v-upload-passport" />
                <UploadBoxV2 label="Variante B: Meldebescheinigung" url={uploads.residence_certificate_url}
                    onUploaded={(u) => setUrl("residence_certificate_url", u)}
                    onClear={() => clearUrl("residence_certificate_url")}
                    hints={HINTS_RESIDENCE}
                    testid="v-upload-residence" />
            </div>

            <div className="mt-3 text-[11px]" data-testid="v-id-completeness">
                {hasAnyVariant ? (
                    <span className="text-green-700 font-bold">
                        ✓ {hasIdCard ? "Ausweis komplett" : "Pass + Meldebescheinigung komplett"}
                    </span>
                ) : (
                    <span className="text-amber-700 font-bold">
                        ⚠ Bitte beide Felder einer Variante hochladen.
                    </span>
                )}
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-2">
                {canRequest && (
                    <Button onClick={requestVerification} disabled={requesting} className="flex-1 bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider" data-testid="v-request-verification">
                        <ShieldQuestion className="w-4 h-4 mr-2" />
                        {requesting
                            ? "Sende…"
                            : (status === "rejected" ? "Erneut zur Prüfung einreichen" : "Prüfung beantragen")}
                    </Button>
                )}
            </div>
        </div>
    );
}
